/**
 * Spectral color science: CIE 1931 color matching functions and spectral-to-RGB conversion.
 * 32 channels spanning 380-780nm at 12.5nm spacing.
 */

export const CHANNEL_COUNT = 32;
export const WAVELENGTH_START = 380e-9; // meters
export const WAVELENGTH_END = 780e-9;
export const WAVELENGTH_STEP = (WAVELENGTH_END - WAVELENGTH_START) / (CHANNEL_COUNT - 1);

/** Get wavelength in meters for a given channel index (0-31) */
export function channelToWavelength(channel: number): number {
  return WAVELENGTH_START + channel * WAVELENGTH_STEP;
}

/** Get the nearest channel index for a given wavelength in meters */
export function wavelengthToChannel(wavelength: number): number {
  const idx = Math.round((wavelength - WAVELENGTH_START) / WAVELENGTH_STEP);
  return Math.max(0, Math.min(CHANNEL_COUNT - 1, idx));
}

/**
 * CIE 1931 x̄(λ) color matching function — Gaussian approximation.
 * Input: wavelength in nanometers.
 */
export function cie_x(nm: number): number {
  const t1 = (nm - 442.0) * ((nm < 442.0) ? 0.0624 : 0.0374);
  const t2 = (nm - 599.8) * ((nm < 599.8) ? 0.0264 : 0.0323);
  const t3 = (nm - 501.1) * ((nm < 501.1) ? 0.0490 : 0.0382);
  return 0.362 * Math.exp(-0.5 * t1 * t1) +
         1.056 * Math.exp(-0.5 * t2 * t2) -
         0.065 * Math.exp(-0.5 * t3 * t3);
}

/**
 * CIE 1931 ȳ(λ) color matching function — Gaussian approximation.
 */
export function cie_y(nm: number): number {
  const t1 = (nm - 568.8) * ((nm < 568.8) ? 0.0213 : 0.0247);
  const t2 = (nm - 530.9) * ((nm < 530.9) ? 0.0613 : 0.0322);
  return 0.821 * Math.exp(-0.5 * t1 * t1) +
         0.286 * Math.exp(-0.5 * t2 * t2);
}

/**
 * CIE 1931 z̄(λ) color matching function — Gaussian approximation.
 */
export function cie_z(nm: number): number {
  const t1 = (nm - 437.0) * ((nm < 437.0) ? 0.0845 : 0.0278);
  const t2 = (nm - 459.0) * ((nm < 459.0) ? 0.0385 : 0.0725);
  return 1.217 * Math.exp(-0.5 * t1 * t1) +
         0.681 * Math.exp(-0.5 * t2 * t2);
}

/** Precomputed CIE lookup table: [channel][x, y, z] */
export const CIE_TABLE: Float32Array = (() => {
  const table = new Float32Array(CHANNEL_COUNT * 3);
  for (let i = 0; i < CHANNEL_COUNT; i++) {
    const nm = channelToWavelength(i) * 1e9;
    table[i * 3]     = cie_x(nm);
    table[i * 3 + 1] = cie_y(nm);
    table[i * 3 + 2] = cie_z(nm);
  }
  return table;
})();

// XYZ to linear sRGB matrix (D65 illuminant)
const M_XYZ_TO_SRGB = [
   3.2406, -1.5372, -0.4986,
  -0.9689,  1.8758,  0.0415,
   0.0557, -0.2040,  1.0570,
];

/** sRGB gamma companding */
function srgbGamma(c: number): number {
  if (c <= 0.0031308) return 12.92 * c;
  return 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

export interface RGB {
  r: number; // 0-1
  g: number;
  b: number;
}

/**
 * Convert a 32-channel spectral power distribution to sRGB.
 * @param spd Float32Array of length 32 (intensity per channel)
 * @param exposure Exposure multiplier (default 1)
 * @returns RGB values in 0-1 range, clamped
 */
export function spectralToRGB(spd: Float32Array, exposure: number = 1): RGB {
  // Integrate S(λ) × CIE matching functions via Riemann sum
  let X = 0, Y = 0, Z = 0;
  const dLambda = WAVELENGTH_STEP * 1e9; // nm step for integration weighting

  for (let i = 0; i < CHANNEL_COUNT; i++) {
    const s = spd[i];
    if (s === 0) continue;
    X += s * CIE_TABLE[i * 3]     * dLambda;
    Y += s * CIE_TABLE[i * 3 + 1] * dLambda;
    Z += s * CIE_TABLE[i * 3 + 2] * dLambda;
  }

  // Normalize — the integral of ȳ(λ) over visible range ≈ 106.9
  // Scale so a flat unit spectrum maps to roughly Y=1
  const normFactor = 1 / 106.9;
  X *= normFactor * exposure;
  Y *= normFactor * exposure;
  Z *= normFactor * exposure;

  // XYZ → linear sRGB
  const lr = M_XYZ_TO_SRGB[0] * X + M_XYZ_TO_SRGB[1] * Y + M_XYZ_TO_SRGB[2] * Z;
  const lg = M_XYZ_TO_SRGB[3] * X + M_XYZ_TO_SRGB[4] * Y + M_XYZ_TO_SRGB[5] * Z;
  const lb = M_XYZ_TO_SRGB[6] * X + M_XYZ_TO_SRGB[7] * Y + M_XYZ_TO_SRGB[8] * Z;

  // Gamma correction + clamp
  return {
    r: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lr)))),
    g: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lg)))),
    b: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lb)))),
  };
}

/** Convert a single wavelength (meters) + intensity to sRGB.
 *  Uses direct CIE lookup — NOT the spectral integration path,
 *  because a monochromatic line is a delta function. */
export function monochromaticToRGB(wavelength: number, intensity: number = 1): RGB {
  const nm = wavelength * 1e9;
  const X = cie_x(nm) * intensity;
  const Y = cie_y(nm) * intensity;
  const Z = cie_z(nm) * intensity;

  const lr = M_XYZ_TO_SRGB[0] * X + M_XYZ_TO_SRGB[1] * Y + M_XYZ_TO_SRGB[2] * Z;
  const lg = M_XYZ_TO_SRGB[3] * X + M_XYZ_TO_SRGB[4] * Y + M_XYZ_TO_SRGB[5] * Z;
  const lb = M_XYZ_TO_SRGB[6] * X + M_XYZ_TO_SRGB[7] * Y + M_XYZ_TO_SRGB[8] * Z;

  return {
    r: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lr)))),
    g: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lg)))),
    b: Math.max(0, Math.min(1, srgbGamma(Math.max(0, lb)))),
  };
}

/** Convert RGB (0-1) to CSS color string */
export function rgbToCSS(rgb: RGB, alpha: number = 1): string {
  const r = Math.round(rgb.r * 255);
  const g = Math.round(rgb.g * 255);
  const b = Math.round(rgb.b * 255);
  if (alpha < 1) return `rgba(${r},${g},${b},${alpha})`;
  return `rgb(${r},${g},${b})`;
}

/** Create a flat (white) spectrum with given intensity */
export function whiteSpectrum(intensity: number = 1): Float32Array {
  const spd = new Float32Array(CHANNEL_COUNT);
  spd.fill(intensity);
  return spd;
}

/** Create a monochromatic spectrum at the given wavelength */
export function monochromaticSpectrum(wavelength: number, intensity: number = 1): Float32Array {
  const spd = new Float32Array(CHANNEL_COUNT);
  const channel = wavelengthToChannel(wavelength);
  spd[channel] = intensity;
  return spd;
}
